/**
 * Patch-management types — mirror docs/patch-management/03-interface-contract.md.
 *
 * Contract §1/§2/§5/§6 define the enums and the nested result shapes exactly.
 * Contract §9 describes the campaign/stage *detail* payload only loosely
 * ("campaign, stages[] (status, run url, plan summary/hash, approvals,
 * targets/results), counts"); the stage field names below are the smallest
 * compatible reading and every optional member is treated as possibly absent.
 */

// ─── Vocabulary (contract §1) ─────────────────────────────────────────────────

export const PATCH_ENVIRONMENTS = ['dev', 'uat', 'prod'] as const
export type PatchEnvironment = (typeof PATCH_ENVIRONMENTS)[number]

export const STAGE_STATUSES = [
  'not_started',
  'plan_dispatched',
  'plan_running',
  'awaiting_approval',
  'approved',
  'execute_dispatched',
  'executing',
  'validating',
  'accepted',
  'failed',
  'rejected',
  'cancelled',
] as const
export type StageStatus = (typeof STAGE_STATUSES)[number]

export const COMPLIANCE_STATES = ['compliant', 'noncompliant', 'unverified', 'unsupported'] as const
export type ComplianceState = (typeof COMPLIANCE_STATES)[number]

export type OsType = 'windows' | 'linux'

// ─── Patch scope (contract §2) ────────────────────────────────────────────────

export const WINDOWS_CLASSIFICATIONS = [
  'Critical',
  'Security',
  'UpdateRollup',
  'FeaturePack',
  'ServicePack',
  'Definition',
  'Tools',
  'Updates',
] as const
export type WindowsClassification = (typeof WINDOWS_CLASSIFICATIONS)[number]

export const LINUX_CLASSIFICATIONS = ['Critical', 'Security', 'Other'] as const
export type LinuxClassification = (typeof LINUX_CLASSIFICATIONS)[number]

export const REBOOT_SETTINGS = ['IfRequired', 'NeverReboot', 'AlwaysReboot'] as const
export type RebootSetting = (typeof REBOOT_SETTINGS)[number]

export interface PatchScope {
  schema_version: 1
  product: string
  classifications: {
    windows: WindowsClassification[]
    linux: LinuxClassification[]
  }
  kb_numbers_to_include: string[]
  kb_numbers_to_exclude: string[]
  package_name_masks_to_include: string[]
  package_name_masks_to_exclude: string[]
  /** `YYYY-MM-DD` or null. */
  max_patch_publish_date: string | null
  reboot_setting: RebootSetting
  applications: string[]
}

// ─── Targets (contract §3) ────────────────────────────────────────────────────

/** Which tag an environment's VMs were copied from, and when. Only present on an environment created from a tag. */
export interface PatchTargetSource {
  mode: 'tag'
  tag_id: number | null
  tag_name: string
  connection_id: number | null
  /** RFC 3339 UTC. The tag can change afterwards; the campaign keeps the VMs it had at this moment. */
  snapshot_at: string
}

/** What the API returns for one environment: an explicit resource group and VM list, never a wildcard or a subscription. */
export interface PatchTarget {
  /** Empty when the VMs are in more than one resource group (then see `resource_groups` and `vms`). */
  resource_group: string
  vm_names: string[]
  /** Only for an environment made from a tag whose VMs span several resource groups. */
  resource_groups?: string[]
  vms?: { resource_group: string; name: string }[]
  source?: PatchTargetSource
}

/** A create request that lists the VMs itself. */
export interface ExplicitTargetRequest {
  resource_group: string
  vm_names: string[]
}

/** A create request that names a tag. The portal copies the tag's VMs into the campaign when it is created. */
export interface TagTargetRequest {
  target_mode: 'tag'
  tag_name: string
}

export type PatchTargetRequest = ExplicitTargetRequest | TagTargetRequest

export function isTagTarget(target: PatchTargetRequest | undefined): target is TagTargetRequest {
  return !!target && 'target_mode' in target && target.target_mode === 'tag'
}

/** The `environments` object of the create request: dev required, uat/prod optional. */
export interface PatchTargets {
  dev: PatchTargetRequest
  uat?: PatchTargetRequest
  prod?: PatchTargetRequest
}

// ─── Create request (contract §9 POST campaigns/) ────────────────────────────

export interface CreatePatchCampaignRequest {
  name: string
  description: string
  product: string
  change_ticket: string
  patch_scope: PatchScope
  environments: PatchTargets
}

// ─── Plan, assessment, risk, results (contract §5/§6/§10) ────────────────────

export interface PlanSummary {
  add: number
  change: number
  destroy: number
}

export interface PatchPlan {
  has_changes: boolean
  plan_hash: string | null
  summary: PlanSummary
  destructive: boolean
  scope_expanding: boolean
  /** Set by the portal when destructive or scope_expanding (contract §5). */
  requires_additional_review?: boolean
}

export interface MissingCounts {
  critical: number
  security: number
  other: number
}

export interface VmRecovery {
  protected: boolean
  last_recovery_point: string | null
  age_hours: number | null
}

export interface VmAssessment {
  name: string
  os_type: OsType
  power_state: string | null
  patch_mode: string | null
  /**
   * Contract §11.7: false = the VM could not be assessed (powered off / assessment failed).
   * Then `missing`, `reboot_pending` and `last_assessed` are null — never zeros.
   * (Optional only so an older payload does not crash the page; see `isAssessed`.)
   */
  assessed?: boolean
  missing: MissingCounts | null
  reboot_pending: boolean | null
  last_assessed: string | null
  assessment_fresh: boolean
  tags?: Record<string, string>
  /** null = not checked / unavailable — never assumed healthy. */
  recovery: VmRecovery | null
  /** Individual updates behind the C/S/O counts. Empty when Azure stored counts only. */
  patches?: { name: string; kb_or_package: string; classification: string; reboot?: string; state?: string }[]
}

export type RiskTier = 'low' | 'medium' | 'high' | 'critical'

export interface VmRisk {
  vm_name: string
  risk_tier: RiskTier
  blocked: boolean
  reasons: string[]
  controls?: Record<string, unknown>
}

export interface UnsupportedApplication {
  application: string
  mechanism: string
  reason: string
}

export interface RolloutPlan {
  batches: string[][]
  unsupported_applications: UnsupportedApplication[]
}

export type TargetResultStatus = 'succeeded' | 'failed' | 'skipped'

export type ApplicationState = 'verified' | 'unsupported' | 'unverified' | 'excluded'

/** Contract §6: application status is a separate fact from the VM's OS compliance_state. */
export interface ApplicationStatus {
  application: string
  mechanism: string
  state: ApplicationState
  reason?: string | null
}

export interface PatchEntry {
  name: string
  kb_or_package: string
  classification: string
  state: string
}

export interface ValidationCheck {
  check: string
  mandatory: boolean
  passed: boolean
  detail: string | null
}

export interface PatchTargetResult {
  vm_name: string
  os_type: OsType
  batch: number | null
  status: TargetResultStatus
  operation_id?: string | null
  patch_status?: string | null
  installed: number
  failed: number
  excluded: number
  not_selected: number
  pending: number
  reboot_status?: string | null
  maintenance_window_exceeded?: boolean
  patches?: PatchEntry[]
  validation?: ValidationCheck[]
  post_assessment?: {
    missing: MissingCounts | null
    reboot_pending: boolean | null
    assessed_at: string | null
  } | null
  /** OS patch state only; applications are in `application_status` (contract §6). */
  compliance_state: ComplianceState
  /** Why the portal arrived at that state (e.g. what made a VM unverified). */
  compliance_notes?: string[] | string | null
  application_status?: ApplicationStatus[]
  /** Dry-run rows are stored `unverified` and never count towards acceptance (§11.1). */
  dry_run?: boolean
  unknown_state?: boolean
  skipped_reason?: string | null
  error?: string | null
}

export interface AcceptanceUnsupportedApplication {
  vm_name?: string | null
  application: string
  mechanism: string
  reason: string
}

/**
 * The portal's acceptance verdict (contract §11.1). The runner's own figures are informational
 * (`reported_*`); when the portal refuses acceptance `problems[]` names every reason.
 */
export interface AcceptanceResult {
  passed: boolean
  failed_mandatory?: string[]
  /** Portal-computed share of targets whose OS compliance_state is compliant. */
  compliance_pct?: number | null
  threshold_pct?: number | null
  unsupported_applications?: AcceptanceUnsupportedApplication[]
  problems?: string[]
  reported_passed?: boolean | null
  reported_compliance_pct?: number | null
  /** Shape not fixed by the contract: a string or a list of strings. */
  compliance_notes?: string[] | string | null
}

/** Outcome of a dry run (contract §11.1): the stage returns to `approved`, never `accepted`. */
export interface DryRunResult {
  /** false = the dry run did not pass (the stage then fails rather than returning to approved). */
  passed?: boolean | null
  problems?: string[]
  /**
   * One entry per target VM: `{vm_name, status, validation[], error, skipped_reason}`. Other shapes
   * (strings, `{check|gate|name, passed|status, detail|message}`) are tolerated.
   */
  preflight?: unknown[]
  summary?: Record<string, unknown> | null
  duration_seconds?: number | null
  completed_at?: string | null
}

export type RunCancelState = 'none' | 'accepted' | 'failed' | 'unresolved'

/** Contract §11.3: what happened when cancel tried to stop the GitHub run. */
export interface RunCancel {
  state: RunCancelState
  detail?: string | null
  run_url?: string | null
}

export interface StageFailure {
  stage?: string | null
  reason: string
  retry_safe?: boolean
}

// ─── Approvals & audit (contract §9) ─────────────────────────────────────────

export type ApprovalDecision = 'approved' | 'rejected'

export interface PatchApproval {
  id: number
  /** Approver e-mail. */
  approver: string
  decision: ApprovalDecision
  notes: string
  /** Hashes the approver saw (immutable record). */
  scope_hash: string
  plan_hash: string | null
  acknowledged_additional_review?: boolean
  acknowledged_outage?: boolean
  /** The approver-set window stored with the approval (§11.4); prod only. */
  maintenance_window?: MaintenanceWindow | null
  created_at: string
}

export interface PatchAuditEvent {
  id: number | string
  actor: string
  action: string
  before?: unknown
  after?: unknown
  request_id?: string | null
  created_at: string
}

// ─── Stage & campaign ────────────────────────────────────────────────────────

export interface MaintenanceWindow {
  /** RFC 3339 UTC. */
  start: string
  end: string
}

/** Installed and pending counts read from Azure while an install is running. */
export interface InstallProgress {
  installed: number
  pending: number
  failed: number
  not_selected: number
  phase: string
  started_at?: string | null
}

/** One runner step recorded by a plan or execute callback. */
export interface RunnerStageStep {
  status: string
  message: string
  at: string | null
  progress?: InstallProgress
}

/** One post-install check. Status is passed, failed, running, or not_checked. */
export interface PostPatchCheck {
  check: string
  status: string
  mandatory: boolean
  detail: string
}

export interface PatchStage {
  id: number
  environment: PatchEnvironment
  status: StageStatus
  run_id?: string | null
  run_url?: string | null
  /** null for a stage that is not part of the campaign (no targets) or has no plan yet. */
  targets?: PatchTarget | null
  plan?: PatchPlan | null
  approvals: PatchApproval[]
  assessments: VmAssessment[]
  assessment_limitations: string[]
  risks: VmRisk[]
  rollout?: RolloutPlan | null
  results: PatchTargetResult[]
  runner_stages?: Record<string, RunnerStageStep>
  acceptance?: AcceptanceResult | null
  failure?: StageFailure | null
  dry_run?: boolean
  dry_run_result?: DryRunResult | null
  /** The approver-set window (§11.4). Execute only reads it. */
  maintenance_window?: MaintenanceWindow | null
  /** §11.5: in flight but silent for too long. */
  stale?: boolean
  last_callback_at?: string | null
  /** §11.5: dispatch outcome unknown — cannot be dispatched again, can be cancelled. */
  dispatch_unconfirmed?: boolean
  dispatch_attempt?: number
  run_cancel?: RunCancel | null
  /** VMs of a tag-targeted stage left out of the last execute because they no longer qualified. */
  skipped_targets?: SkippedTarget[]
  /** Checks recorded after the install. Empty until post-patch validation runs. */
  post_validation?: PostPatchCheck[]
  started_at?: string | null
  finished_at?: string | null
  updated_at?: string | null
}

export interface SkippedTarget {
  vm_name: string
  resource_id: string
  reason: string
}

export interface PatchCampaign {
  /** `PCH-<YYYY>-<NNNN>` */
  id: string
  name: string
  description: string
  product: string
  change_ticket: string
  /** Requester e-mail (display name in requested_by_name when the server sends it). */
  requested_by: string
  requested_by_name?: string | null
  patch_scope: PatchScope
  scope_hash: string
  stages: PatchStage[]
  counts?: Record<string, number>
  created_at: string
  updated_at?: string | null
}

// ─── Me, advisor, approver assignments ───────────────────────────────────────

export interface PatchMe {
  email: string
  can_request: boolean
  approver_for: PatchEnvironment[]
  is_patch_admin: boolean
}

export interface AdvisorResult {
  available: boolean
  summary: string
  priorities: string[]
  missing_prerequisites: string[]
  invocation_id: string | number | null
}

export interface ApproverAssignment {
  id: number
  environment: PatchEnvironment
  user_email: string
  /** Inactive rows stay in the list after an assignment is removed. */
  active: boolean
}

// ─── Request bodies for stage actions ────────────────────────────────────────

/** Contract §9/§11.4. `plan_hash` and `scope_hash` are compare-and-set values from the stage on screen. */
export interface ApproveStageBody {
  notes: string
  plan_hash?: string | null
  scope_hash?: string
  /** Required for prod. */
  maintenance_window?: MaintenanceWindow
  acknowledge_outage?: boolean
  acknowledge_additional_review?: boolean
}

export interface RejectStageBody {
  notes: string
}

/** Contract §9: execute takes only `dry_run`; the window comes from the approval. */
export interface ExecuteStageBody {
  dry_run?: boolean
}

export interface ScheduleStageBody {
  start: string
  end: string
  timezone: string
  idempotency_key: string
}

export interface PatchScheduleRow {
  id: number
  campaign_id: string
  environment: PatchEnvironment
  status: string
  start_at: string
  end_at: string
  timezone: string
  idempotency_key: string
  fired_at: string | null
  detail: { note?: string; error?: string }
}

export interface AzureSubscriptionConfig {
  id: number
  tenant_id: string
  subscription_id: string
  display_name: string
  auth_method: string
  client_id: string
  managed_identity_name: string
  credential_ref: string
  cloud: string
  resource_group_filter: string
  region_filter: string
  tag_filters: Record<string, string>
  validation_status: string
  validation_detail: { checks?: { name: string; status: string; detail: string; remediation: string }[] }
  last_validated_at: string | null
  last_discovery_at: string | null
}

/** What the server reports after a saved connection is deleted. */
export interface DeletedSubscription {
  id: number
  subscription_id: string
  display_name: string
  resources_removed: number
}

export interface PatchRecommendationRow {
  id: number
  update_name: string
  kb_or_package: string
  classification: string
  recommendation: string
  priority: string
  rationale: string
  confidence: string
  confidence_meaning: string
  evidence: unknown[]
  source: string
}

/** A named, fixed set of discovered VMs (see the Manage tags panel). */
export interface VmTagMember {
  id: number
  resource_id: string
  vm_name: string
  resource_group: string
  region: string
  environment: string
  added_at: string
}

export interface VmTag {
  id: number
  name: string
  description: string
  connection_id: number
  subscription_id: string
  connection_name: string
  member_count: number
  resource_groups: string[]
  members: VmTagMember[]
  /** Ids of the active campaigns that were made from this tag. A tag in use cannot be deleted. */
  used_by: string[]
  created_by: string
  created_at: string
  updated_at: string
}

export type TagMode = 'create' | 'add' | 'replace'

export interface ExcludedVm {
  id: number
  name: string
  reason: string
}

/** What the server did about Azure resource tags: nothing unless WRITE_AZURE_TAGS is on. */
export interface AzureTagsReport {
  enabled: boolean
  written?: number
  failed?: { resource_id: string; error: string }[]
}

export interface TagWriteResult {
  tag: VmTag
  excluded?: ExcludedVm[]
  azure_tags?: AzureTagsReport
}

export interface TagResolutionVm {
  id: number
  resource_id: string
  vm_name: string
  resource_group: string
  region: string
  environment: string
  power_state: string
  eligible: boolean
  in_latest_discovery: boolean
  problems: string[]
}

/** A tag's VMs with their live state, and whether a campaign could be created from it right now. */
export interface TagResolution {
  id: number
  name: string
  connection_id: number
  connection_name: string
  resource_groups: string[]
  vms: TagResolutionVm[]
  ready: boolean
  blockers: string[]
}

export interface PatchInventoryResource {
  id: number
  /** Names of the tags this machine is in. */
  tags: string[]
  subscription_id: string
  name: string
  resource_id: string
  resource_group: string
  region: string
  resource_type: string
  os_type: string
  os_version: string
  vm_size: string
  environment: string
  application: string
  power_state: string
  aum_status: string
  last_assessed: string | null
  pending_count: number | null
  reboot_status: string
  compliance_status: string
  last_install_result: string
  eligible: boolean
  exclusion_reason: string
  assessment: {
    assessed?: boolean
    package_level?: boolean
    limitation?: string | null
    missing?: Record<string, number> | null
    /** 'running' while Azure is still assessing; absent once a result is stored. */
    state?: 'running'
    started_at?: string | null
  }
  patches: { name?: string; kb_or_package?: string; classification?: string; reboot?: string; state?: string }[]
  recommendations: PatchRecommendationRow[]
}

export interface PrePatchCheck {
  resource_name: string
  check: string
  status: string
  mandatory: boolean
  detail: string
}

export interface CampaignReport {
  campaign_id: string
  name: string
  change_ticket: string
  status: string
  note: string
  stages: {
    environment: string
    status: string
    run_id?: string | null
    run_url?: string | null
    dry_run?: boolean
    failure: { reason?: string } | null
    acceptance: unknown
    steps?: { name: string; status: string; message: string }[]
    results: { vm_name: string; status: string; compliance_state: string; installed: number; failed: number; error: string | null; skipped_reason?: string | null }[]
  }[]
  schedules: { id: number; environment: string; status: string; start_at: string; end_at: string }[]
  prechecks: PrePatchCheck[]
}
